import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    Grid,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Chip,
    IconButton,
    CircularProgress,
    Snackbar,
    FormHelperText,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { workshopDomain } from '../../util/domain';

const CouponManagement = () => {
    const navigate = useNavigate();
    const [coupons, setCoupons] = useState([]);
    const [events, setEvents] = useState([]);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        description: '',
        discountType: 'PERCENTAGE',
        discountValue: '',
        maxDiscount: '',
        minPurchaseAmount: '',
        maxUses: '',
        eventHashId: '',
        validFrom: new Date(),
        validUntil: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        isActive: true
    });

    // Check user role and authentication on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');

        // Check if user has Admin role
        const roles = user.roles || [];
        console.log("roles :", roles);
        const isAdmin = roles.includes('Admin');

        if (!isAdmin) {
            navigate('/admin/home');
            return;
        }

        fetchCoupons();
        fetchEvents();
    }, [navigate]);

    const fetchCoupons = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/coupons`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setCoupons(response.data.coupons);
            }
        } catch (error) {
            setError('Failed to fetch coupons');
        }
    };

    const fetchEvents = async () => {
        try {
            const response = await axios.get(`${workshopDomain}/api/events`);
            if (response.data.success) {
                setEvents(response.data.events);
            }
        } catch (error) {
            console.error('Failed to fetch events');
        }
    };

    const handleOpen = (coupon = null) => {
        if (coupon) {
            setEditingCoupon(coupon);
            setFormData({
                code: coupon.code,
                description: coupon.description,
                discountType: coupon.discount_type,
                discountValue: coupon.discount_value,
                maxDiscount: coupon.max_discount || '',
                minPurchaseAmount: coupon.min_purchase_amount || '',
                maxUses: coupon.max_uses || '',
                eventHashId: coupon.event_hash_id || '',
                validFrom: new Date(coupon.valid_from),
                validUntil: new Date(coupon.valid_until),
                isActive: coupon.is_active
            });
        } else {
            setEditingCoupon(null);
            setFormData({
                code: '',
                description: '',
                discountType: 'PERCENTAGE',
                discountValue: '',
                maxDiscount: '',
                minPurchaseAmount: '',
                maxUses: '',
                eventHashId: '',
                validFrom: new Date(),
                validUntil: new Date(new Date().setMonth(new Date().getMonth() + 1)),
                isActive: true
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setError('');
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        console.log("name :", name, "value :", value, "checked :", checked);
        setFormData({
            ...formData,
            [name]: name === 'isActive' ? checked : value
        });
    };

    const handleDateChange = (name) => (date) => {
        setFormData({
            ...formData,
            [name]: date
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            if (editingCoupon) {
                await axios.put(`${workshopDomain}/api/coupons/${editingCoupon.hash_id}`, formData, config);
            } else {
                await axios.post(`${workshopDomain}/api/coupons`, formData, config);
            }

            handleClose();
            fetchCoupons();
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to save coupon');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Container maxWidth="lg">
                <Box sx={{ mt: 4, mb: 4 }}>
                    <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Grid item>
                            <Typography variant="h4" component="h1">
                                Coupon Management
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpen()}
                            >
                                Create Coupon
                            </Button>
                        </Grid>
                    </Grid>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    <Grid container spacing={3}>
                        {coupons.map((coupon) => (
                            <Grid item xs={12} md={6} key={coupon.hash_id}>
                                <Card>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box>
                                                <Typography variant="h6" gutterBottom>
                                                    {coupon.code}
                                                </Typography>
                                                <Chip
                                                    label={coupon.is_active ? 'Active' : 'Inactive'}
                                                    color={coupon.is_active ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            </Box>
                                            <Typography variant="h6" color="primary">
                                                {coupon.discount_type === 'PERCENTAGE'
                                                    ? `${coupon.discount_value}%`
                                                    : `₹${coupon.discount_value}`}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            {coupon.description}
                                        </Typography>
                                        <Box sx={{ mt: 2 }}>
                                            {coupon.event_title && (
                                                <Typography variant="body2">
                                                    <strong>Event:</strong> {coupon.event_title}
                                                </Typography>
                                            )}
                                            {coupon.max_discount && (
                                                <Typography variant="body2">
                                                    <strong>Max Discount:</strong> ₹{coupon.max_discount}
                                                </Typography>
                                            )}
                                            {coupon.min_purchase_amount && (
                                                <Typography variant="body2">
                                                    <strong>Min Purchase:</strong> ₹{coupon.min_purchase_amount}
                                                </Typography>
                                            )}
                                            <Typography variant="body2">
                                                <strong>Usage:</strong> {coupon.current_uses || 0}
                                                {coupon.max_uses ? `/${coupon.max_uses}` : ' (Unlimited)'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Valid:</strong> {formatDate(coupon.valid_from)} - {formatDate(coupon.valid_until)}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                    <CardActions>
                                        <Button
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => handleOpen(coupon)}
                                        >
                                            Edit
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                        <DialogTitle>
                            {editingCoupon ? 'Edit Coupon' : 'Create Coupon'}
                        </DialogTitle>
                        <DialogContent>
                            <Box component="form" noValidate sx={{ mt: 2 }}>
                                <Grid container spacing={2}>
                                    {!editingCoupon && (
                                        <Grid item xs={12}>
                                            <TextField
                                                required
                                                fullWidth
                                                name="code"
                                                label="Coupon Code"
                                                value={formData.code}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            name="description"
                                            label="Description"
                                            value={formData.description}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Discount Type</InputLabel>
                                            <Select
                                                name="discountType"
                                                value={formData.discountType}
                                                onChange={handleChange}
                                                label="Discount Type"
                                            >
                                                <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                                                <MenuItem value="FIXED">Fixed Amount</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            fullWidth
                                            type="number"
                                            name="discountValue"
                                            label={formData.discountType === 'PERCENTAGE' ? 'Discount Percentage' : 'Discount Amount'}
                                            value={formData.discountValue}
                                            onChange={handleChange}
                                            InputProps={{
                                                startAdornment: formData.discountType === 'FIXED' ? '₹' : '',
                                                endAdornment: formData.discountType === 'PERCENTAGE' ? '%' : ''
                                            }}
                                        />
                                    </Grid>
                                    {formData.discountType === 'PERCENTAGE' && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                name="maxDiscount"
                                                label="Maximum Discount"
                                                value={formData.maxDiscount}
                                                onChange={handleChange}
                                                InputProps={{
                                                    startAdornment: '₹'
                                                }}
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            name="minPurchaseAmount"
                                            label="Minimum Purchase Amount"
                                            value={formData.minPurchaseAmount}
                                            onChange={handleChange}
                                            InputProps={{
                                                startAdornment: '₹'
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            name="maxUses"
                                            label="Maximum Uses"
                                            value={formData.maxUses}
                                            onChange={handleChange}
                                            helperText="Leave empty for unlimited uses"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Event (Optional)</InputLabel>
                                            <Select
                                                name="eventHashId"
                                                value={formData.eventHashId}
                                                onChange={handleChange}
                                                label="Event (Optional)"
                                            >
                                                <MenuItem value="">All Events</MenuItem>
                                                {events.map((event) => (
                                                    <MenuItem key={event.hash_id} value={event.hash_id}>
                                                        {event.title}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DateTimePicker
                                            label="Valid From"
                                            value={formData.validFrom}
                                            onChange={handleDateChange('validFrom')}
                                            renderInput={(params) => <TextField {...params} fullWidth required />}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DateTimePicker
                                            label="Valid Until"
                                            value={formData.validUntil}
                                            onChange={handleDateChange('validUntil')}
                                            renderInput={(params) => <TextField {...params} fullWidth required />}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.isActive}
                                                    onChange={handleChange}
                                                    name="isActive"
                                                />
                                            }
                                            label="Active"
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>Cancel</Button>
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </Container>
        </LocalizationProvider>
    );
};

export default CouponManagement; 